import { useIntl } from '@/intl'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'

import ServiceBox from '@/scopes/checkout/components/services/ServiceBox'

import { useSetParticipantsBadge } from '@/scopes/checkout/domain/hooks'
import { CheckoutParticipant } from '@/scopes/checkout/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { mqMin, space, SpaceBetween, tok, tokPx } from '@sunwebgroup/ui-components'
import { DateField, TextField, Typography } from '@sunwebgroup/ui-components/uds'
import { sub } from 'date-fns'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { styled } from 'styled-components'
import { createAncillariesAvailableEvent } from '../domain/analytics'
import { isValidDateFormat, namePattern, useSmartFieldStatus, validateAgeBetweenDates } from './helpers/Validations'

const lengthValidationFieldName = 1
const lengthValidationFieldBirthdate = 10 // YYYY-MM-DD format
const defaultMinBirthdate = sub(new Date(), { years: 18 })
const defaultMaxBirthdate = sub(new Date(), { days: 1 })
const defaultBirthdate = '1990-01-01'

interface FormValues {
  participantBadge: {
    id: string
    name: string
    birthdate?: string
  }[]
}

interface FieldErrors {
  participantBadge?: Record<string, { name?: { message: string }, birthdate?: { message: string } }>
}

const ParticipantFieldsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
  @media ${mqMin('primitives_breakpoint_m_min')} {
    flex-direction: row;
    justify-content: space-between;
  }
`
const ParticipantFormField = styled.div`
  flex: 1 1 0;
  width: 100%;
`

const ParticipantFormFieldDate = styled.div`
  flex: 1 1 0;
  width: 100%;
  @media ${mqMin('primitives_breakpoint_m_min')} {
    max-width: ${tokPx('primitives_area_size_200')};
  }
`
const RoomName = styled(Typography)`
  color: ${tok('semantics_text_color')};
`

export function ParticipantsBadgeForm() {
  const intl = useIntl()
  const { participants, rooms, setParticipantsBadge } = useSetParticipantsBadge()
  const { sendEvent } = useAnalytics()
  const hasBeenSent = React.useRef(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  const blurTimeoutRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (hasBeenSent.current)
      return
    sendEvent(
      createAncillariesAvailableEvent({
        result: 'participantsBadge',
      }),
    )
    hasBeenSent.current = true
  }, [sendEvent, hasBeenSent])

  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== null) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  const form = useForm<FormValues>({
    defaultValues: {
      participantBadge: Object.values(participants ?? {})?.map(({ id, name, birthdate }) => {
        return {
          id,
          name: name ?? '',
          birthdate: birthdate ?? defaultBirthdate,
        }
      }),
    },
    mode: 'onBlur',
  })

  const onSubmit = (result: FormValues) => {
    setParticipantsBadge(result.participantBadge)
    sendEvent({
      event: 'Participants Badge',
      validated: true,
      description: 'Save participants badge form',
      element: 'on blur',
      result: 'save',
      interaction: true,
    })
  }

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_description' })}
      glyph={getIconForService('participantsBadge')}
    >
      <FormProvider {...form}>
        <form
          ref={formRef}
          onBlur={() => {
            blurTimeoutRef.current = window.setTimeout(() => {
              const formElement = formRef.current
              const activeElement = document.activeElement

              if (formElement && activeElement && !formElement.contains(activeElement)) {
                form.handleSubmit(onSubmit)()
              }
            }, 0)
          }}
          onFocus={() => {
            if (blurTimeoutRef.current !== null) {
              clearTimeout(blurTimeoutRef.current)
            }
          }}
        >
          <SpaceBetween direction="y" spacing={space('primitives_space_16')}>
            {Object.values(rooms ?? {}).map(({ id: roomId, name: roomName, participantIds }) => (
              <SpaceBetween key={roomId} direction="y" spacing={space('primitives_space_16')}>
                {!isEmpty(rooms) && Object.values(rooms ?? {}).length > 1 && roomName
                  ? (
                      <RoomName as="p" $variant="body-l-regular-tight">
                        {roomName}
                      </RoomName>
                    )
                  : null}
                {participantIds.map(participantId => (
                  <SpaceBetween direction="y" key={participantId} spacing={space('primitives_space_8')}>
                    <ParticipantRow participantId={participantId} />
                  </SpaceBetween>
                ))}
              </SpaceBetween>
            ))}
            {isEmpty(rooms) && (
              <>
                {Object.values(participants ?? {})?.map(participant => (
                  <SpaceBetween direction="y" key={participant.id} spacing={space('primitives_space_8')}>
                    <ParticipantRow participantId={participant.id} />
                  </SpaceBetween>
                ))}
              </>
            )}
          </SpaceBetween>
        </form>
      </FormProvider>
    </ServiceBox>
  )
}

function ParticipantRow({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { participants, ageRestrictions } = useSetParticipantsBadge()
  const currentParticipant = participants ? participants[participantId] : undefined
  const participantIndex = Object.keys(participants ?? {}).findIndex(id => id === participantId)
  const currentAgeRestriction
    = ageRestrictions && currentParticipant ? ageRestrictions[currentParticipant.ageCategory] : undefined

  const minBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.min) : defaultMinBirthdate
  const maxBirthdate = currentAgeRestriction ? new Date(currentAgeRestriction.max) : defaultMaxBirthdate
  const { register, getFieldState, control, setValue, trigger, formState, watch } = useFormContext()
  const errorsTyped = formState.errors as FieldErrors

  const nameFieldState = getFieldState(`participantBadge.${participantIndex}.name`, formState)
  const errorFieldName = errorsTyped?.participantBadge?.[participantIndex]?.name
  const watchedParticipantNameFieldValue = watch(`participantBadge.${participantIndex}.name`) ?? ''
  const nameFieldMatchingPattern = namePattern.test(watchedParticipantNameFieldValue ?? '')

  const statusParticipantFieldName = useSmartFieldStatus({
    value: watchedParticipantNameFieldValue,
    error: errorFieldName,
    isDirty: nameFieldState?.isDirty ?? false,
    fieldLengthValidation: lengthValidationFieldName,
    isSubmitting: true, // Assuming the form is always submitted on blur
    matchPattern: nameFieldMatchingPattern,
  })

  const shouldValidateName = (newValue: string) => {
    return Boolean(errorFieldName) && namePattern.test(newValue) && newValue.length >= lengthValidationFieldName
  }

  const birthdateFieldState = getFieldState(`participantBadge.${participantIndex}.birthdate`, formState)
  const errorFieldBirthdate = errorsTyped?.participantBadge?.[participantIndex]?.birthdate
  const watchedParticipantBirthdateFieldValue = watch(`participantBadge.${participantIndex}.birthdate`) ?? ''

  const statusParticipantFieldBirthdate = useSmartFieldStatus({
    value: watchedParticipantBirthdateFieldValue,
    error: errorFieldBirthdate,
    isDirty: birthdateFieldState?.isDirty ?? false,
    fieldLengthValidation: lengthValidationFieldBirthdate,
    isSubmitting: true, // Assuming the form is always submitted on blur
    matchPattern: validateAgeBetweenDates(watchedParticipantBirthdateFieldValue ?? '', minBirthdate, maxBirthdate),
  })

  const participantIndexByAgeCategory = getAgeCategoryIndex(participantId, participants ?? {})

  if (!currentParticipant)
    return null

  return (
    <ParticipantFieldsRow>
      <input {...register(`participantBadge.${participantIndex}.id`, { value: participantId })} type="hidden" />
      <ParticipantFormField>
        <Controller
          name={`participantBadge.${participantIndex}.name`}
          control={control}
          rules={{
            pattern: {
              value: namePattern,
              message: intl.formatMessage({ id: 'checkout_steps_participants_badge_form_pattern_field' }),
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(e) => {
                const newValue = e.target.value
                field.onChange(e)
                setValue(`participantBadge.${participantIndex}.name`, newValue, {
                  shouldValidate: shouldValidateName(newValue),
                })
              }}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget) && field.value?.length >= lengthValidationFieldName) {
                  return trigger(`participantBadge.${participantIndex}.name`)
                }
                field.onBlur()
              }}
              label={intl.formatMessage(
                { id: 'checkout_steps_participants_badge_form_name_label' },
                {
                  id: participantIndexByAgeCategory,
                  ageCategory: intl.formatMessage({
                    id: `age_category_${currentParticipant.ageCategory.toLowerCase()}`,
                  }),
                },
              )}
              status={statusParticipantFieldName}
              feedbackText={errorsTyped?.participantBadge?.[participantIndex]?.name?.message}
              helpText={intl.formatMessage({ id: 'checkout_steps_participants_badge_form_name_help_text' })}
            />
          )}
        />
      </ParticipantFormField>
      <ParticipantFormFieldDate>
        <Controller
          name={`participantBadge.${participantIndex}.birthdate`}
          control={control}
          rules={{
            validate: {
              validateAge: (value: unknown) => {
                if (typeof value !== 'string') {
                  return 'Date not valid'
                }
                if (!value) {
                  return true
                }
                const isValidAge = validateAgeBetweenDates(value, minBirthdate, maxBirthdate)
                return (
                  isValidAge
                  || intl.formatMessage({ id: 'checkout_steps_participants_badge_form_birthdate_validation_text' })
                )
              },
            },
          }}
          render={({ field }) => (
            <DateField
              {...field}
              onChange={(e) => {
                const newValue = e.target.value
                field.onChange(e)
                setValue(`participantBadge.${participantIndex}.birthdate`, newValue, {
                  shouldValidate:
                    Boolean(errorFieldBirthdate)
                    && isValidDateFormat(newValue)
                    && newValue.length >= lengthValidationFieldBirthdate,
                })
              }}
              onBlur={() => {
                if (field.value && field.value.length >= lengthValidationFieldBirthdate) {
                  return trigger(`participantBadge.${participantIndex}.birthdate`)
                }
                field.onBlur()
              }}
              maxDate={maxBirthdate.toISOString().split('T')[0]}
              minDate={minBirthdate.toISOString().split('T')[0]}
              label={intl.formatMessage(
                { id: 'checkout_steps_participants_badge_form_birthdate_text' },
                {
                  id: participantIndexByAgeCategory,
                  ageCategory: intl.formatMessage({
                    id: `age_category_${currentParticipant.ageCategory.toLowerCase()}_short`,
                  }),
                },
              )}
              status={statusParticipantFieldBirthdate}
              feedbackText={errorsTyped?.participantBadge?.[participantIndex]?.birthdate?.message}
              attributes={{ 'data-empty': `${!field.value}` }}
            />
          )}
        />
      </ParticipantFormFieldDate>
    </ParticipantFieldsRow>
  )
}

function getAgeCategoryIndex(
  participantId: string,
  participants: Record<string, CheckoutParticipant>,
): number | undefined {
  const participant = participants[participantId]
  if (!participant)
    return undefined

  const sameCategoryParticipants = Object.values(participants).filter(p => p.ageCategory === participant.ageCategory)

  const sortedById = sameCategoryParticipants.sort((a, b) => a.id.localeCompare(b.id))

  return sortedById.findIndex(p => p.id === participantId) + 1
}
